import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';

/* The event categories — townhall, workshop, family day, after-work.

   Its own PAGE, reached from the "+" at the end of the filter row on the calendar, because that
   is where you are standing when you notice a category is missing. Not on the programmes screen:
   the catalogue belongs to the whole company rather than to any one programme, so putting it
   under one of them said the wrong thing about what it is.

   Renaming "After-work" here renames it on every event that carries it — which is the point of
   a category, and the reason it is edited in one place.

   ★ Retiring a category never deletes the row. Events already carry it, and deleting would
   quietly rewrite what those events WERE — a townhall would stop having been a townhall. It
   drops out of the pickers and the filters; the history keeps it. */
const CategoryList = ({ tags, onChanged }) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const run = (promise) => {
    setBusy(true);
    setNotice(null);
    promise
      .then((res) => { onChanged(res.tags || []); setAdding(null); setEditing(null); })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const save = (row) => {
    if (!row.name.trim()) return;
    run(row.id
      ? apiRequest('PUT', `/company/event-tags/${row.id}`, row)
      : apiRequest('POST', '/company/event-tags', row));
  };

  const retire = (tag) => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('retireCategoryConfirm', { name: tag.name }))) return;
    run(apiRequest('DELETE', `/company/event-tags/${tag.id}`));
  };

  const form = (row, set) => (
    <div className="bravos-form">
      <div className="bravos-form__row">
        <input
          type="text"
          value={row.emoji || ''}
          maxLength={2}
          style={{ flex: '0 0 3.5rem', textAlign: 'center' }}
          placeholder="🎉"
          onChange={(e) => set({ ...row, emoji: e.target.value })}
        />
        <input
          type="text"
          value={row.name}
          maxLength={60}
          style={{ flex: '1 1 10rem' }}
          placeholder={t('categoryNamePlaceholder')}
          onChange={(e) => set({ ...row, name: e.target.value })}
        />
        <input
          type="color"
          value={row.color || '#002335'}
          style={{ flex: '0 0 3rem', padding: '2px' }}
          onChange={(e) => set({ ...row, color: e.target.value })}
        />
      </div>
      <div className="bravos-form__row">
        <button
          type="button"
          className="bravos-submit"
          onClick={() => save(row)}
          disabled={busy || !row.name.trim()}
        >
          {busy ? t('pleaseWait') : t('save')}
        </button>
        <button
          type="button"
          className="bravos-submit bravos-submit--ghost"
          onClick={() => { setAdding(null); setEditing(null); }}
        >
          {t('cancel')}
        </button>
      </div>
    </div>
  );

  return (
    <Card>
      <Title my="1" className="mt-2">
        <Icon name="tags" theme="primary" className="me-2" />
        {t('eventTags')}
      </Title>
      <p className="bravos-q__hint mb-2">{t('categoriesNote')}</p>

      {tags.map((tag) => (
        editing && editing.id === tag.id ? (
          <div key={tag.id}>{form(editing, setEditing)}</div>
        ) : (
          <div key={tag.id} className="ce-person">
            <span className="ce-person__body">
              <span className="ce-person__name">
                {tag.emoji ? `${tag.emoji} ` : ''}{tag.name}
              </span>
            </span>
            <span
              className="ce-pill"
              style={{ background: tag.color || '#e9ecef', width: '1.6rem' }}
            >&nbsp;</span>
            <button
              type="button"
              className="ce-person__act"
              onClick={() => { setEditing({ ...tag }); setAdding(null); }}
              disabled={busy}
            >
              {t('edit')}
            </button>
            <button
              type="button"
              className="bravos-member__remove"
              onClick={() => retire(tag)}
              disabled={busy}
              title={t('retireCategory')}
            >
              <Icon name="eye-crossed" />
            </button>
          </div>
        )
      ))}

      {adding ? form(adding, setAdding) : (
        <button
          type="button"
          className="bravos-submit bravos-submit--ghost"
          onClick={() => { setAdding({ name: '', emoji: '', color: '' }); setEditing(null); }}
        >
          <Icon name="plus" /> {t('newCategory')}
        </button>
      )}
      {notice && <p className="bravos-cal__notice">{notice}</p>}
    </Card>
  );
};

const EventCategories = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tags, setTags] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/company/event-tags')
      .then((res) => setTags(res.tags || []))
      // Employees can filter by categories but not own them, so a non-admin lands back on the
      // calendar rather than on an empty screen.
      .catch(() => navigate('/company-events'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/company-events" />
        <Title my="1" className="mt-3">
          <Icon name="tags" theme="primary" className="me-2" />
          {t('eventTags')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('categoriesNote')}</p>
      </Card>

      {tags === null
        ? <Card><span className="auth__spinner" /></Card>
        : <CategoryList tags={tags} onChanged={setTags} />}
    </Base>
  );
};

export default EventCategories;
